import type { StoredSkiBand } from "../conditions/ski-v3-carriers";
export type MountainSport = "piste" | "off-piste" | "touring";
export const MOUNTAIN_SPORTS = ["piste", "off-piste", "touring"] as const;
export const MOUNTAIN_SPORT_LABELS: Record<MountainSport, string> = {
  piste: "Piste skiing",
  "off-piste": "Off-piste",
  touring: "Ski touring",
};
export type MountainLocation = {
  slug: string;
  latitude: number;
  longitude: number;
  elevationM: number | null;
  timezone: string;
};
export type MountainFrame = {
  date: string;
  hour: number;
  utcMs: number | null;
  fetchedAt: string | null;
  issuedAt: null;
  past: boolean;
  reason: string | null;
  elevationM: number | null;
  weather: {
    temperatureC: number | null;
    windMs: number | null;
    gustMs: number | null;
    fromDeg: number | null;
    cloud: number | null;
    visibilityM: number | null;
    snowfallCm: number | null;
    rainMm: number | null;
    depthM: number | null;
    profile: readonly { heightM: number; temperatureC: number }[];
  };
  sun: { azimuthDeg: number; elevationDeg: number } | null;
  scores: Partial<
    Record<
      MountainSport,
      {
        score: number | null;
        isDay: boolean;
        closed: boolean;
        revision: number;
      }
    >
  >;
  bands: Partial<Record<MountainSport, readonly StoredSkiBand[]>>;
};
export type MountainForecast = {
  generatedAt: string;
  location: MountainLocation;
  days: { date: string; hours: MountainFrame[] }[];
};
export type MountainView = {
  id: "riffelberg" | "gornergrat";
  label: string;
  latitude: number;
  longitude: number;
  position: readonly [number, number, number];
  yawRad: number;
  pitchRad: number;
  elevationM: number;
  centre: readonly [number, number];
  walkRadiusM: number;
};
export type MountainAsset = {
  path: string;
  bytes: number;
  sha256: string;
  kind: "mesh" | "height" | "texture";
  compression?: "gzip";
  decodedBytes?: number;
  view?: MountainView["id"];
  tier?: "near";
  size?: 512 | 1024 | 2048;
};
export type MountainPackage = {
  schemaVersion: 1;
  slug: "zermatt";
  version: string;
  reviewStatus: "draft" | "accepted";
  timezone: "Europe/Zurich";
  horizontalCrs: "EPSG:2056";
  verticalDatum: "LN02";
  originEastingM: number;
  originNorthingM: number;
  gridToTrueNorthRad: number;
  bounds: readonly [number, number, number, number];
  assets: readonly MountainAsset[];
  viewpoints: readonly MountainView[];
  attribution: readonly { label: string; url: string }[];
  heightNote: string;
  colliders: readonly (readonly (readonly [number, number])[])[];
};
export type MountainEnvironment = {
  sunDirection: readonly [number, number, number];
  daylight: number;
  cloud: number;
  visibilityM: number;
  windMs: number;
  windDirection: readonly [number, number];
  snowfall: number;
  rain: number;
  weatherReference: boolean;
  precipitationReference: boolean;
  surfaceReference: boolean;
  surface: "powder" | "groomed" | "hard" | "corn" | "slush" | "chop" | null;
  coverage: number;
  surfaceBandM: number | null;
  coverageBands: readonly { elevationM: number; coverage: number }[];
  surfaceLabel: string;
};
export type MountainEngine = {
  setEnvironment(environment: MountainEnvironment): void;
  setViewpoint(id: MountainView["id"]): void;
  setPaused(paused: boolean): void;
  setQuality(tier: "low" | "high"): void;
  setMovement(forward: number, right: number): void;
  look(dx: number, dy: number): void;
  resize(width: number, height: number, dpr: number): void;
  dispose(): Promise<void>;
  diagnostics(): { width: number; height: number; gpuBytes: number; triangles: number;
    low: boolean; renderScale: number; meanMs: number; p95Ms: number; frames: number;
    submittedFrames: number; lastSubmittedAt: number;
    gpuTiming: { meanMs: number; p95Ms: number; samples: number } | null };
};
export type CreateMountainEngine = (options: {
  canvas: HTMLCanvasElement;
  pack: MountainPackage;
  signal: AbortSignal;
  onProgress(stage: string, fraction: number): void;
  onFailure(reason: string): void;
  onLowerDetail?(): void;
}) => Promise<MountainEngine>;
